import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Linking } from 'react-native';
import { IMessageBubbleProps, IFileBubbleProps } from '@/types/admin/messages';
import { useConversationUtils } from '@/hooks/admin/useMessages';
import { useAppSelector } from '@/store';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { LazyImage } from '@/components/common/admin/mobile/LazyImage';

export const MessageBubble: React.FC<IMessageBubbleProps> = ({
  message,
}) => {
  const { redesign } = useAdminColors();
  const { components } = useThemeColors();
  const { formatMessageTime } = useConversationUtils();
  // Get current user ID from adminUser state, with fallback to auth.user.id
  const adminUserId = useAppSelector((state) => state.adminUser.id);
  const authUserId = useAppSelector((state) => state.auth.user?.id);
  const currentUserId = (adminUserId && adminUserId !== 0) ? adminUserId : authUserId;

  // Compare as numbers to handle any type coercion issues from API
  const hasSenderId = message.sender?.id != null;
  const hasCurrentUserId = currentUserId != null && currentUserId !== 0;
  const sentByCurrentUser = hasSenderId && hasCurrentUserId
    ? Number(message.sender.id) === Number(currentUserId)
    : false;
  const isAdminMessage = message.isAdmin;

  // Determine sender name
  let senderName = message.sender?.firstName && message.sender?.lastName
    ? `${message.sender.firstName} ${message.sender.lastName}`
    : message.sender?.email || 'Unknown';

  if (sentByCurrentUser) {
    senderName = 'Sent by you';
  }

  const messageTime = formatMessageTime(message.createdAt);

  const sentBubbleColor = components.chat.sentBubble;
  const receivedBubbleColor = components.chat.receivedBubble;

  // Determine bubble background and text colors per orientation
  const isSentAdmin = sentByCurrentUser && isAdminMessage;
  const bubbleBg = isSentAdmin
    ? sentBubbleColor
    : receivedBubbleColor;
  const messageTextColor = isSentAdmin ? '#FFFFFF' : redesign.text.primary;
  const senderNameColor = isSentAdmin ? 'rgba(255,255,255,0.85)' : redesign.text.subtle;
  const timestampColor = isSentAdmin ? 'rgba(255,255,255,0.75)' : redesign.text.subtle;

  return (
    <View
      style={[
        styles.container,
        sentByCurrentUser ? styles.sentContainer : styles.receivedContainer,
      ]}
    >
      <View
        style={[
          styles.bubble,
          { backgroundColor: bubbleBg },
          sentByCurrentUser && !isAdminMessage && {
            borderWidth: 1,
            borderColor: redesign.card.border,
          },
        ]}
      >
        {/* Show sender name for non-admin messages or messages from other admins */}
        {(!isAdminMessage || !sentByCurrentUser) && (
          <Text style={[styles.senderName, { color: senderNameColor }]}>
            {senderName}
          </Text>
        )}

        <Text style={[styles.messageText, { color: messageTextColor }]}>
          {message.content}
        </Text>

        <View style={styles.footer}>
          <Text style={[styles.timestamp, { color: timestampColor }]}>
            {messageTime}
          </Text>

          {/* Show sender name for admin messages at the bottom */}
          {isAdminMessage && sentByCurrentUser && (
            <Text style={[styles.footerSender, { color: timestampColor }]}>
              {senderName}
            </Text>
          )}
        </View>
      </View>
    </View>
  );
};

export const FileBubble: React.FC<IFileBubbleProps> = ({
  file,
  isAdmin,
  onPress,
}) => {
  const { redesign } = useAdminColors();
  const { truncateFileName, getFileExtension } = useConversationUtils();

  const handlePress = () => {
    if (onPress) {
      onPress();
    } else if (file.fileUrl) {
      // Open file URL in browser/external app
      Linking.openURL(file.fileUrl).catch(err => {
        console.error('Failed to open file:', err);
      });
    }
  };

  // Check if file is an image (basic check)
  const isImage = file.type?.startsWith('image/') ||
    file.fileUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

  const fileName = truncateFileName(file.label || file.name || 'Unknown file');
  const fileExtension = file.name ? getFileExtension(file.name) : file.type?.split('/')[1] || '';

  const containerBg = redesign.card.bg;

  return (
    <View
      style={[
        styles.fileContainer,
        isAdmin ? styles.sentContainer : styles.receivedContainer,
      ]}
    >
      <TouchableOpacity
        style={[
          styles.fileBubble,
          { backgroundColor: containerBg, borderColor: redesign.card.border },
          isImage && styles.imageBubble,
        ]}
        onPress={handlePress}
        activeOpacity={0.7}
      >
        {isImage && file.fileUrl ? (
          <View style={styles.imageContainer}>
            <LazyImage
              source={{ uri: file.fileUrl }}
              style={{ ...styles.imagePreview, backgroundColor: containerBg }}
              resizeMode="cover"
              errorPlaceholder={(
                <View style={[styles.imagePlaceholder, { backgroundColor: containerBg }]}>
                  <SVG_COMPONENTS.FOLDER
                    width={24}
                    height={24}
                    color={redesign.text.muted}
                  />
                  <Text style={[styles.imageLabel, { color: redesign.text.muted }]}>
                    {fileName}
                  </Text>
                </View>
              )}
            />
          </View>
        ) : (
          <View style={styles.fileContent}>
            <SVG_COMPONENTS.PAPERCLIP
              width={20}
              height={20}
              color={redesign.text.muted}
              style={styles.fileIcon}
            />
            <View style={styles.fileInfo}>
              <Text style={[styles.fileName, { color: redesign.text.primary }]}>
                {fileName}
              </Text>
              {!!fileExtension && (
                <Text style={[styles.fileExtension, { color: redesign.text.muted }]}>
                  {fileExtension.toUpperCase()}
                </Text>
              )}
            </View>
          </View>
        )}
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 4,
    paddingHorizontal: 16,
  },
  sentContainer: {
    alignItems: 'flex-end',
  },
  receivedContainer: {
    alignItems: 'flex-start',
  },
  bubble: {
    maxWidth: '80%',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 16,
  },
  senderName: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 4,
  },
  messageText: {
    fontSize: 16,
    lineHeight: 20,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: 8,
  },
  timestamp: {
    fontSize: 12,
    opacity: 0.85,
  },
  footerSender: {
    fontSize: 12,
    opacity: 0.85,
    marginLeft: 16,
  },

  // File bubble styles
  fileContainer: {
    marginVertical: 4,
    paddingHorizontal: 16,
  },
  fileBubble: {
    borderRadius: 12,
    padding: 12,
    maxWidth: '80%',
    borderWidth: 1,
  },
  imageBubble: {
    padding: 6,
    overflow: 'hidden',
  },
  imageContainer: {
    borderRadius: 12,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imagePreview: {
    width: 240,
    height: 240,
    borderRadius: 8,
  },
  imagePlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  imageLabel: {
    fontSize: 12,
    marginTop: 8,
    textAlign: 'center',
  },
  fileContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  fileIcon: {
    marginRight: 12,
  },
  fileInfo: {
    flex: 1,
  },
  fileName: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 2,
  },
  fileExtension: {
    fontSize: 12,
  },
});
